import React, { useEffect, useState } from "react";
import { observer } from 'mobx-react-lite';
import { AdminLayout } from "../shared/layout";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CardBox } from "../shared/CardBox";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getWaitingPageStatus,
  updateWaitingPageStatus,
} from "@/data/requests/setting-requests";
import { useSettings } from "@/context/SettingsProvider";


export const SettingScreen = observer(() => {
  const { toast } = useToast();
  const settingsStore = useSettings();
  const [status, setStatus] = useState("inactive");

  useEffect(() => {
    const fetchStatus = async () => {
      const response = await getWaitingPageStatus();
      setStatus(response.data);
    };

    fetchStatus();
    settingsStore.fetchSettings();
  }, [settingsStore]);

  const updateStatus = async (newStatus) => {
    try {
      await updateWaitingPageStatus({ status: newStatus });
      setStatus(newStatus);
      toast({
        title: "Başarılı",
        description: "Bekleme sayfası durumu güncellendi.",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "Bekleme sayfası durumu güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    try {
      await settingsStore.updateSettings(settingsStore.settings);
      toast({
        title: "Başarılı",
        description: "Ayarlar kaydedildi.",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "Ayarlar kaydedilirken bir hata oluştu.",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    settingsStore.setSettings({ ...settingsStore.settings, [name]: value });
  };

  const pageSettings = {
    title: "Settings",
    actions: [
      {
        name: "Save Changes",
        onClick: handleSave,
      },
    ],
  };

  return (
    <AdminLayout title={pageSettings.title} actions={pageSettings.actions}>
      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="social">Contact & Social</TabsTrigger>
          <TabsTrigger value="legal">Legal</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <CardBox
            title="General Settings"
            description="Manage your site's core information"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="site_title">Site Title</Label>
                <Input
                  id="site_title"
                  name="site_title"
                  placeholder="Enter site title"
                  value={settingsStore.settings.site_title || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="site_description">Site Description</Label>
                <Textarea
                  id="site_description"
                  name="site_description"
                  placeholder="Enter site description"
                  value={settingsStore.settings.site_description || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="site_icon">Site Logo URL</Label>
                <div className="flex items-center space-x-4">
                  <img
                    alt="Site Logo"
                    className="rounded-md border bg-muted"
                    height={64}
                    src={settingsStore.settings.site_icon || "/placeholder.svg"}
                    style={{
                      aspectRatio: "1",
                      objectFit: "cover",
                      width: "64px",
                      height: "64px"
                    }}
                  />
                  <Input
                    id="site_icon"
                    name="site_icon"
                    type="text"
                    placeholder="https://example.com/logo.png"
                    value={settingsStore.settings.site_icon || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </CardBox>
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <CardBox
            title="Contact & Social Media"
            description="Manage contact information and social media links"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  placeholder="support@example.com"
                  value={settingsStore.settings.contact_email || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_twitter">Twitter / X URL</Label>
                <Input
                  id="social_twitter"
                  name="social_twitter"
                  placeholder="https://twitter.com/username"
                  value={settingsStore.settings.social_twitter || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_instagram">Instagram URL</Label>
                <Input
                  id="social_instagram"
                  name="social_instagram"
                  placeholder="https://instagram.com/username"
                  value={settingsStore.settings.social_instagram || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="social_linkedin">LinkedIn URL</Label>
                <Input
                  id="social_linkedin"
                  name="social_linkedin"
                  placeholder="https://linkedin.com/company/..."
                  value={settingsStore.settings.social_linkedin || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </CardBox>
        </TabsContent>

        <TabsContent value="legal" className="space-y-4">
          <CardBox
            title="Legal Documents"
            description="Links to your legal documents"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="terms_url">Terms of Service URL</Label>
                <Input
                  id="terms_url"
                  name="terms_url"
                  placeholder="https://example.com/terms"
                  value={settingsStore.settings.terms_url || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="privacy_url">Privacy Policy URL</Label>
                <Input
                  id="privacy_url"
                  name="privacy_url"
                  placeholder="https://example.com/privacy"
                  value={settingsStore.settings.privacy_url || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </CardBox>
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-4">
          <CardBox
            title="Maintenance Mode"
            description="Control access to your application"
          >
            <div className="flex items-center justify-between space-x-2 border p-4 rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="waiting-page" className="text-base">
                  Enable Waiting Page
                </Label>
                <p className="text-sm text-muted-foreground">
                  When enabled, all non-admin users will be redirected to the waiting page.
                </p>
              </div>
              <Switch
                id="waiting-page"
                checked={status === "active"}
                onCheckedChange={(checked) =>
                  updateStatus(checked ? "active" : "inactive")
                }
              />
            </div>
          </CardBox>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
});